/**
 * The human-readable half of a FastAPI `detail`.
 *
 * `detail` is a string on most routes and an OBJECT on the ones that carry a
 * machine-readable code — fastapi-users' own `REGISTER_INVALID_PASSWORD` has
 * always been shaped that way, and DOP-222/DOP-223 added `REGISTER_NO_CLIENT_SCOPE`
 * and `no_client_scope`. Every call site used to do `error.detail || fallback`,
 * and an object is truthy, so `new Error(detail)` rendered the refusal as
 * "[object Object]" — at exactly the moment the refusal exists to be read.
 */

/** The machine-readable code, when the payload carries one. */
export function apiErrorCode(detail: unknown): string | null {
	if (detail && typeof detail === "object" && !Array.isArray(detail)) {
		const code = (detail as { code?: unknown }).code;
		if (typeof code === "string") return code;
	}
	return null;
}

/**
 * A sentence to show a person. Falls back to the code (better than nothing —
 * it is at least searchable) and then to the caller's own default.
 */
export function apiErrorMessage(detail: unknown, fallback: string): string {
	if (typeof detail === "string" && detail) return detail;
	if (detail && typeof detail === "object" && !Array.isArray(detail)) {
		const record = detail as { message?: unknown; reason?: unknown };
		// `message` is ours; `reason` is what fastapi-users uses for the same job.
		if (typeof record.message === "string" && record.message)
			return record.message;
		if (typeof record.reason === "string" && record.reason) return record.reason;
		const code = apiErrorCode(detail);
		if (code) return code;
	}
	return fallback;
}

/** What we say when we have nothing readable to say. */
export const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again.";

const NETWORK_ERROR_MESSAGE =
	"We couldn't reach the server. Check your connection and try again.";

/**
 * A sentence for an HTTP status, for the many responses that carry no `detail`
 * a person could read. "API Error: 500 Internal Server Error" is a line for a
 * log, not for the screen.
 */
export function friendlyStatusMessage(status: number): string {
	if (status === 400 || status === 422)
		return "We couldn't process that request. Please check the details and try again.";
	if (status === 401) return "Your session has expired. Please sign in again.";
	if (status === 403) return "You don't have access to this.";
	if (status === 404) return "We couldn't find what you were looking for.";
	if (status === 408 || status === 504)
		return "That took too long to respond. Please try again.";
	if (status === 409)
		return "That conflicts with something that already exists. Please refresh and try again.";
	if (status === 413) return "That file is too large to upload.";
	if (status === 429)
		return "Too many requests at once. Please wait a moment and try again.";
	if (status >= 500)
		return "Something went wrong on our end. Please try again in a moment.";
	return GENERIC_ERROR_MESSAGE;
}

/**
 * Text that belongs in a console, not in front of a person.
 *
 * Deliberately shaped, not blanket: an earlier cut matched any three-digit
 * number and swallowed real validation copy ("limit must be between 1 and
 * 100"). A status code only reads as a status code when it sits in a status
 * line — next to a reason phrase, the word "status", or an "API Error:" prefix.
 */
const TECHNICAL_PATTERNS: RegExp[] = [
	/\bAPI Error\b/i,
	/^HTTP\/?\d?/i,
	/\bstatus(?:\s+code)?[:\s]+[1-5]\d{2}\b/i, // "status code 503"
	/\b[1-5]\d{2}\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)+/, // "500 Internal Server Error"
	// A reason phrase next to a status code, or standing alone as the whole
	// message. Never loose and case-insensitive: "was not found" and "access is
	// forbidden" are things we say to users on purpose.
	/\b(?:HTTP\s*)?[1-5]\d{2}\s*[:\-—]?\s*(?:Internal Server Error|Bad Request|Bad Gateway|Service Unavailable|Gateway Timeout|Unprocessable (?:Entity|Content)|Not Found|Forbidden|Unauthorized|Too Many Requests|Payload Too Large|Method Not Allowed)\b/i,
	/\b(Internal Server Error|Bad Gateway|Service Unavailable|Gateway Timeout|Unprocessable (Entity|Content)|Too Many Requests|Payload Too Large|Method Not Allowed)\b/,
	/^(Not Found|Forbidden|Unauthorized|Bad Request)\.?$/i,
	// A machine code the backend never turned into prose: LOGIN_BAD_CREDENTIALS.
	/^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+$/,
	// Any exception class name, ours or the backend's: ValueError, KeyError,
	// IntegrityError, sqlalchemy.exc.OperationalError, ClientResponseError.
	/\b[A-Za-z_][\w.]*(?:Error|Exception)\b/,
	/\bTraceback\b/,
	/\bat\s+\S+\s+\(.*:\d+:\d+\)/, // stack frame
	/\.(t|j)sx?:\d+/, // source location
	/\.py["']?,\s*line\s+\d+/i, // python frame
	/\[object \w+\]/,
	/(Cannot read propert|is not a function|is not defined|undefined is not)/i,
	/^\s*</, // an HTML error page
	/^\{|^\[/, // a raw JSON blob
];

/**
 * The complete messages browsers use when the request never reached a server.
 *
 * Anchored on purpose: an unanchored /load failed/i turned "Upload failed
 * because the file is encrypted" into a connectivity notice, which sends the
 * user to check their wifi over an encrypted spreadsheet.
 */
const NETWORK_PATTERNS: RegExp[] = [
	/^(?:TypeError:\s*)?Failed to fetch\.?$/i, // Chrome
	/^NetworkError when attempting to fetch resource\.?$/i, // Firefox
	/^Load failed\.?$/i, // Safari
	/^Network request failed\.?$/i,
	/^The network connection was lost\.?$/i,
	/^The Internet connection appears to be offline\.?$/i,
	/^net::ERR_[A-Z_]+$/,
	/^ERR_(NETWORK|INTERNET_DISCONNECTED|CONNECTION_[A-Z_]+)$/,
];

function isTechnical(message: string): boolean {
	return TECHNICAL_PATTERNS.some((pattern) => pattern.test(message));
}

function messageOf(error: unknown): string {
	const raw =
		typeof error === "string"
			? error
			: error instanceof Error
				? error.message
				: "";
	return raw.trim();
}

/**
 * Turn anything a `catch` block can hand you into a sentence a user can read.
 *
 * Deny-list: text that smells like a status line, an exception, or a stack
 * trace is swapped for `fallback`; everything else passes through, because a
 * server `detail` is usually written for a person. Use this where the thrown
 * text comes from our own API layer. Where it can carry a raw `str(exc)` —
 * the chat stream — use `publicSafeErrorMessage` instead.
 */
export function friendlyErrorMessage(
	error: unknown,
	fallback: string = GENERIC_ERROR_MESSAGE,
): string {
	const message = messageOf(error);
	if (!message) return fallback;
	if (NETWORK_PATTERNS.some((pattern) => pattern.test(message)))
		return NETWORK_ERROR_MESSAGE;
	if (isTechnical(message)) return fallback;
	return message;
}


/**
 * fastapi-users answers a failed login with a bare machine code — the response
 * body is literally `{"detail": "LOGIN_BAD_CREDENTIALS"}`. Nothing downstream
 * can turn that into English, so the map lives here, at the auth boundary.
 */
const AUTH_ERROR_MESSAGES: Readonly<Record<string, string>> = {
	LOGIN_BAD_CREDENTIALS: "That email or password is incorrect.",
	LOGIN_USER_NOT_VERIFIED:
		"This account hasn't been verified yet. Check your email for the verification link.",
	REGISTER_USER_ALREADY_EXISTS: "An account with that email already exists.",
	REGISTER_INVALID_PASSWORD: "Please choose a stronger password.",
	RESET_PASSWORD_BAD_TOKEN:
		"That password reset link has expired. Request a new one.",
	RESET_PASSWORD_INVALID_PASSWORD: "Please choose a stronger password.",
	VERIFY_USER_BAD_TOKEN:
		"That verification link has expired. Request a new one.",
	VERIFY_USER_ALREADY_VERIFIED:
		"This account is already verified — you can sign in.",
	UPDATE_USER_EMAIL_ALREADY_EXISTS: "That email is already in use.",
	UPDATE_USER_INVALID_PASSWORD: "Please choose a stronger password.",
};

/**
 * The message for a failed auth call: our copy for a code we recognize, the
 * server's own sentence when it wrote one (fastapi-users puts the password
 * rule in `reason`), and the caller's fallback for anything else — including
 * an unrecognized code, which must never reach the screen as itself.
 */
/** SCREAMING_SNAKE with no spaces: a constant, not a sentence. */
const MACHINE_CODE = /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+$/;

export function authErrorMessage(detail: unknown, fallback: string): string {
	// A string `detail` is a code only when it is shaped like one — plenty of
	// our own auth refusals arrive as ordinary prose and must survive.
	const code =
		apiErrorCode(detail) ??
		(typeof detail === "string" && MACHINE_CODE.test(detail) ? detail : null);
	const supplied = apiErrorMessage(detail, "");
	const mapped = code ? AUTH_ERROR_MESSAGES[code] : undefined;
	if (mapped) {
		// `supplied === code` means the payload was the bare code and nothing more.
		return supplied && supplied !== code
			? friendlyErrorMessage(supplied, mapped)
			: mapped;
	}
	return friendlyErrorMessage(supplied === code ? "" : supplied, fallback);
}

/**
 * OAuth providers hand back their failure in the callback URL, which means the
 * "error" here is a query parameter — attacker-controlled text arriving on a
 * page that renders it and re-broadcasts it to the opener. Only recognized
 * codes get words; everything else gets the fallback, unrendered and unsent.
 */
const OAUTH_ERROR_MESSAGES: Readonly<Record<string, string>> = {
	access_denied: "Sign-in was cancelled.",
	consent_required: "Sign-in needs your consent to continue.",
	login_required: "Please sign in with your provider and try again.",
	interaction_required: "Your provider needs you to finish signing in there.",
	invalid_scope: "This app asked for permissions your provider refused.",
	invalid_request: "Your provider rejected the sign-in request.",
	invalid_grant: "That sign-in attempt expired. Please try again.",
	unauthorized_client: "This app isn't authorized to sign you in that way.",
	unsupported_response_type: "Your provider rejected the sign-in request.",
	server_error: "Your provider had a problem signing you in. Please try again.",
	temporarily_unavailable:
		"Your provider is temporarily unavailable. Please try again in a moment.",
	admin_policy_enforced:
		"Your organization's policy blocked this sign-in. Ask an administrator.",
	org_internal: "This account isn't allowed to sign in to this workspace.",
	// Ours, not the provider's: the backend callback sends it for an account
	// that exists but is held for approval (PENDING_APPROVAL_CODE).
	account_pending_approval:
		"Your account is awaiting approval. Someone at Doppler needs to activate it before you can sign in.",
};

/** The copy the OAuth callback shows when it recognizes nothing. */
export const OAUTH_GENERIC_MESSAGE =
	"We couldn't complete sign-in. Please try again.";

export function oauthErrorMessage(code: unknown, fallback: string): string {
	if (typeof code !== "string") return fallback;
	return OAUTH_ERROR_MESSAGES[code.trim().toLowerCase()] ?? fallback;
}

/**
 * Every sentence this module can produce. Membership is what makes a message
 * safe to show on the paths below — the text is ours, not the server's.
 */
const CLIENT_GENERATED_MESSAGES: ReadonlySet<string> = new Set([
	GENERIC_ERROR_MESSAGE,
	NETWORK_ERROR_MESSAGE,
	...[400, 401, 403, 404, 408, 409, 413, 422, 429, 500].map(
		friendlyStatusMessage,
	),
	...Object.values(AUTH_ERROR_MESSAGES),
	...Object.values(OAUTH_ERROR_MESSAGES),
	OAUTH_GENERIC_MESSAGE,
]);

/**
 * The allow-list twin of `friendlyErrorMessage`, for text that reaches us
 * straight out of a backend `str(exc)`.
 *
 * The chat stream forwards whatever the agent raised — no `detail` contract,
 * no guarantee anyone wrote it for a reader. A deny-list is the wrong default
 * there: it lets through everything nobody thought to pattern-match. So this
 * one inverts it — only sentences this module itself produced (an apiClient
 * failure that already went through the status map) pass; anything else
 * becomes the caller's fallback.
 *
 * Deferred: a structured error-code contract across the API would let the
 * backend mark a message as user-facing and end the guessing. Out of scope
 * for DOP-268 — see the ticket's follow-ups.
 */
export function publicSafeErrorMessage(
	error: unknown,
	fallback: string = GENERIC_ERROR_MESSAGE,
): string {
	const message = messageOf(error);
	if (!message) return fallback;
	if (NETWORK_PATTERNS.some((pattern) => pattern.test(message)))
		return NETWORK_ERROR_MESSAGE;
	return CLIENT_GENERATED_MESSAGES.has(message) ? message : fallback;
}

/**
 * The message for a non-OK response: the server's own `detail` when it reads
 * like a sentence, the status's plain-English equivalent otherwise.
 */
export function friendlyApiErrorMessage(
	status: number,
	detail: unknown,
): string {
	const statusMessage = friendlyStatusMessage(status);
	const sentence = apiErrorMessage(detail, "");
	// `apiErrorMessage` falls back to the machine code when the payload has no
	// prose. A code is searchable, not readable — the status sentence is better.
	const readable = sentence === apiErrorCode(detail) ? "" : sentence;
	return friendlyErrorMessage(readable, statusMessage);
}
